import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * KPI Card — Carte indicateur clé (Refonte UX 07/05/2026)
 *
 * Inspiré de la maquette : icône colorée à droite, valeur grande, label,
 * variation vs période précédente avec flèche colorée.
 *
 * Props:
 *   label: string (ex: "Opérations totales")
 *   value: string | number (ex: 128 ou "2 450 000")
 *   unit?: string (ex: "FCFA")
 *   icon: LucideIcon
 *   variant: 'emerald' | 'amber' | 'blue' | 'red' | 'green' | 'slate'
 *   change?: number  (% variation, ex: 12 ou -3)
 *   changeLabel?: string  (ex: "vs période précédente")
 *   onClick?: () => void
 *   loading?: boolean
 */
const VARIANT_STYLES = {
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
  green:   { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
  amber:   { bg: 'bg-amber-50',   icon: 'text-amber-600' },
  blue:    { bg: 'bg-blue-50',    icon: 'text-blue-600' },
  red:     { bg: 'bg-red-50',     icon: 'text-red-600' },
  slate:   { bg: 'bg-slate-100',  icon: 'text-slate-600' },
};

const KpiCard = ({
  label,
  value,
  unit,
  icon: Icon,
  variant = 'emerald',
  change,
  changeLabel = 'vs période précédente',
  onClick,
  loading = false,
  testId,
}) => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.emerald;
  const positive = change > 0;
  const negative = change < 0;
  const TrendIcon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const isInteractive = !!onClick;

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white rounded-2xl border border-slate-200/70 p-5 transition-all',
        'flex flex-col gap-3',
        isInteractive && 'cursor-pointer hover:shadow-md hover:border-slate-300',
      )}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500 font-medium">{label}</p>
        {Icon && (
          <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', styles.bg)}>
            <Icon className={cn('w-5 h-5', styles.icon)} />
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-1.5">
        {loading ? (
          <div className="h-9 w-24 bg-slate-100 rounded animate-pulse" />
        ) : (
          <>
            <span className="text-3xl font-bold text-slate-900 tracking-tight">{value ?? '—'}</span>
            {unit && <span className="text-sm font-medium text-slate-500">{unit}</span>}
          </>
        )}
      </div>

      {change !== undefined && change !== null && !loading && (
        <div className="flex items-center gap-1.5 text-xs">
          <span className={cn(
            'inline-flex items-center gap-0.5 font-semibold',
            positive && 'text-emerald-600',
            negative && 'text-red-600',
            !positive && !negative && 'text-slate-500',
          )}>
            <TrendIcon className="w-3 h-3" />
            {positive && '+'}{change}%
          </span>
          <span className="text-slate-400">{changeLabel}</span>
        </div>
      )}
    </div>
  );
};

export default KpiCard;
